import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, FileText, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

// XHR-based API helper
const apiRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url);
    
    const headers = { ...getAuthHeaders(), ...(options.headers || {}) };
    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });
    
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        let response;
        try {
          response = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch (e) {
          response = { detail: xhr.responseText || 'Request failed' };
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true, data: response, status: xhr.status });
        } else {
          resolve({ ok: false, data: response, status: xhr.status });
        }
      }
    };
    
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(options.body || null);
  });
};

const termsContent = `
# TERMS AND CONDITIONS FOR kbpartner.in PARTNER PROGRAM

**Effective Date: January 20, 2026**

## PREAMBLE

These Terms and Conditions ("Agreement") constitute a legally binding contract between you ("Partner," "you," or "your") and **Kilobeaters Bespoke Meals Pvt. Ltd.** ("Company," "we," "us," or "our"), a company incorporated under the Companies Act, 2013, with its registered office in Maharashtra, India.

By accessing the kbpartner.in website and/or clicking the "I Accept" button during the registration process, you acknowledge that you have carefully read, understood, and unconditionally agree to be bound by all terms, conditions, and provisions contained herein. This Agreement is effective as of the date of your acceptance and shall remain in effect until terminated in accordance with Section 6 below.

**If you do not agree with any provision of this Agreement, you must immediately cease your participation in the Program and refrain from using the kbpartner.in platform.**

---

## 1. DEFINITIONS AND INTERPRETATIONS

For the purposes of this Agreement, the following terms shall have the meanings ascribed to them:

**"Referral"** means any new customer who registers for the Company's services through the Partner's unique referral link or code provided by the Company.

**"Transaction"** means a completed purchase or subscription of the Company's services by a Referral, for which payment has been received by the Company.

**"Commission"** means the compensation payable to the Partner for each Transaction generated by their Referrals, calculated at the applicable commission rate for the Partner's tier.

**"Revenue"** means the aggregate monetary value of all Transactions generated by a Partner's Referrals during a calendar month, calculated in Indian Rupees (INR).

**"Monthly Retainer"** means the fixed monthly fee payable to Partners in the Gold and Platinum tiers, regardless of the number of Transactions generated.

**"Equity-Linked Instruments"** means equity-related securities, warrants, or other instruments that may be issued to Partners in the Gold and Platinum tiers, calculated as a percentage of their annual Revenue, subject to compliance with applicable securities laws and regulations.

**"Tier"** means the classification of a Partner's status within the Program, determined by the monthly Revenue generated by their Referrals.

---

## 2. PARTNER PROGRAM STRUCTURE

### 2.1 Program Overview

The kbpartner.in Partner Program is a referral-based compensation model designed to incentivize Partners to promote the Company's services. The Program operates on a tiered structure with three distinct levels: Silver, Gold, and Platinum. Each tier offers progressively higher commission rates, retainer fees, and additional benefits based on the Partner's monthly Revenue performance.

### 2.2 Partner Tiers and Compensation Structure

A Partner's tier is determined solely by the total monthly Revenue generated by their Referrals. The tier structure is as follows:

| **Tier** | **Monthly Revenue Threshold** | **Commission Rate** | **Monthly Retainer** | **Equity-Linked Instruments** |
|:---|:---|:---|:---|:---|
| **Silver** | Up to Rs. 75,000 | 15% | Not Applicable | Not Eligible |
| **Gold** | Rs. 75,001 to Rs. 5,00,000 | 20% | Rs. 15,000 | Eligible* |
| **Platinum** | Above Rs. 5,00,000 | 25% | Rs. 25,000 | Eligible* |

*Subject to minimum tenure requirement and separate Equity-Linked Instruments Agreement

### 2.3 Tier Advancement, Stability, and Demotion

(a) **Tier Advancement:** A Partner's tier is recalculated at the end of each calendar month. If a Partner's Revenue for that month exceeds the threshold for a higher tier, they will be promoted to that tier, effective from the first day of the following month.

(b) **Tier Stability Period:** Once a Partner is promoted to the Gold or Platinum tier, they shall retain that tier status for a minimum period of three (3) consecutive months ("Tier Stability Period"), regardless of whether their monthly Revenue meets the tier's threshold during this period.

(c) **Tier Demotion:** Following the conclusion of the Tier Stability Period, if the Partner's average monthly Revenue over the three-month period does not meet the minimum threshold for their current tier, they will be demoted to the appropriate lower tier.

### 2.4 Minimum Tenure Requirement for Gold and Platinum Tiers

Notwithstanding the Revenue thresholds specified in Section 2.2, a Partner must maintain a minimum continuous tenure of **one (1) year** in the Program to be eligible for the Gold and Platinum tiers and their associated benefits, including the monthly retainer and equity-linked instruments.

---

## 3. PARTNER ELIGIBILITY AND ACCEPTANCE

### 3.1 Eligibility Criteria

To be eligible for participation in the Program, you must satisfy the following conditions:

(a) You must be at least eighteen (18) years of age and possess the legal capacity to enter into a binding contract;

(b) You must provide accurate, complete, and truthful information during the registration process;

(c) You must not be subject to any legal restrictions or prohibitions that would prevent your participation in the Program;

(d) You must comply with all applicable laws and regulations in your jurisdiction.

---

## 4. COMMISSION AND PAYMENT TERMS

### 4.1 Commission Calculation

Commissions are calculated as a percentage of the Revenue generated by a Partner's Referrals during each calendar month, at the rate applicable to the Partner's tier. Commissions are earned only when a Transaction is completed and payment has been received by the Company.

### 4.2 Payment Schedule and Request Process

(a) **Payment Dates:** The Company shall process payments twice a month, on the 15th and 30th of each calendar month.

(b) **Payment Request Portal:** To receive payment, the Partner must submit a payment request through the designated portal on the kbpartner.in website.

(c) **Request Submission:** Payment requests must be submitted at least three (3) business days prior to a Payment Date to be processed on that date.

### 4.3 Payment Thresholds

(a) **Minimum Payment Threshold:** A Partner may only submit a payment request if their accrued and unpaid commission balance is equal to or greater than **Rs. 1,000** (one thousand Indian Rupees).

(b) **Maximum Payment Request:** The maximum amount that can be requested in a single payment is **Rs. 2,00,000** (two lakh Indian Rupees).

---

## 5. PARTNER OBLIGATIONS AND CONDUCT

### 5.1 Compliance with Laws

The Partner agrees to comply with all applicable laws, regulations, and ordinances in their jurisdiction.

### 5.2 Prohibited Activities

The Partner shall not:

(a) Engage in fraudulent or deceptive practices;

(b) Use automated tools, bots, or scripts to artificially generate Referrals or Transactions;

(c) Engage in spam, unsolicited communications, or harassment of potential customers;

(d) Violate any intellectual property rights of the Company or third parties;

(e) Use the Program for any unlawful purpose or in violation of this Agreement.

### 5.3 Confidentiality Obligations

The Partner acknowledges that in the course of participating in the Program, they may have access to confidential and proprietary information of the Company. The Partner agrees to maintain the confidentiality of all such information.

### 5.4 Data Protection and Privacy

The Partner shall comply with all applicable data protection and privacy laws, including the Information Technology Act, 2000, and the Digital Personal Data Protection Act, 2023.

---

## 6. TERM AND TERMINATION

### 6.1 Duration

This Agreement shall continue in full force and effect until terminated by either party.

### 6.2 Termination by Either Party

Either party may terminate this Agreement at any time, with or without cause, by providing thirty (30) calendar days' written notice to the other party.

### 6.3 Immediate Termination for Cause

The Company may terminate this Agreement immediately, without providing advance notice, if the Partner engages in fraudulent, illegal, or unethical conduct, or violates any material provision of this Agreement.

---

## 7. LIMITATION OF LIABILITY AND INDEMNIFICATION

### 7.1 Limitation of Liability

TO THE FULLEST EXTENT PERMITTED UNDER THE LAWS OF INDIA, IN NO EVENT SHALL THE COMPANY, ITS PARENT COMPANY, SUBSIDIARIES, AFFILIATES, INVESTORS, CO-FOUNDERS, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR REPRESENTATIVES BE LIABLE TO THE PARTNER FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES.

### 7.2 Indemnification by Partner

The Partner agrees to defend, indemnify, and hold harmless the Company, its parent company, subsidiaries, affiliates, investors, co-founders, officers, directors, employees, agents, and representatives from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses arising from or related to the Partner's use of and access to the Program.

---

## 8. GOVERNING LAW AND DISPUTE RESOLUTION

### 8.1 Governing Law

This Agreement shall be governed by and construed in accordance with the laws of India.

### 8.2 Jurisdiction and Venue

The Partner irrevocably and unconditionally submits to the exclusive jurisdiction of the courts located in Mumbai, Maharashtra, India.

---

## 9. MISCELLANEOUS PROVISIONS

### 9.1 Entire Agreement

This Agreement constitutes the entire agreement between the Partner and the Company regarding the Program.

### 9.2 Amendments and Modifications

The Company reserves the right to modify, amend, or supplement this Agreement at any time and for any reason.

### 9.3 Relationship of Parties

Nothing in this Agreement shall create a partnership, joint venture, agency, or employment relationship between the Partner and the Company.

---

## 10. ACKNOWLEDGMENT AND ACCEPTANCE

**BY CLICKING THE "I ACCEPT" BUTTON, YOU ACKNOWLEDGE THAT:**

1. You have read this Agreement in its entirety and understand all of its terms and conditions;

2. You have had the opportunity to seek legal counsel regarding this Agreement;

3. You voluntarily and unconditionally agree to be bound by all provisions of this Agreement;

4. You understand that this Agreement contains limitations of liability and indemnification provisions;

5. You accept the exclusive jurisdiction of the courts in Mumbai, Maharashtra, India for any disputes arising from this Agreement.

---

**CONTACT INFORMATION**

For any questions, concerns, or notices regarding this Agreement, please contact:

**Kilobeaters Bespoke Meals Pvt. Ltd.**
Email: partners@kilobeaters.com
Website: www.kbpartner.in

---

**END OF TERMS AND CONDITIONS**
`;

export default function TermsAndConditions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [hasRead, setHasRead] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthAndTCStatus();
  }, []);

  const checkAuthAndTCStatus = async () => {
    try {
      // First check if user is authenticated
      const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      
      if (!sessionToken) {
        // Not logged in, redirect to login
        navigate('/login');
        return;
      }
      
      setIsAuthenticated(true);
      
      // Then check T&C status
      const result = await apiRequest(`${API}/auth/tc-status`);
      if (result.ok && result.data.tc_accepted) {
        setAlreadyAccepted(true);
        // Redirect to dashboard if already accepted
        navigate('/dashboard');
      } else if (!result.ok && result.status === 401) {
        // Session invalid, redirect to login
        localStorage.removeItem('session_token');
        sessionStorage.removeItem('session_token');
        navigate('/login');
        return;
      }
    } catch (error) {
      console.error('Error checking T&C status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 50;
    if (isAtBottom) {
      setHasRead(true);
    }
  };

  const handleAccept = async () => {
    if (!agreed) {
      toast.error('Please check the box to confirm you agree to the Terms and Conditions');
      return;
    }

    setAccepting(true);
    try {
      const result = await apiRequest(`${API}/auth/accept-tc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (result.ok) {
        toast.success('Terms and Conditions accepted successfully!');
        navigate('/dashboard');
      } else {
        throw new Error(result.data.detail || 'Failed to accept Terms and Conditions');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to accept Terms and Conditions');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    // Clear session and redirect to login
    localStorage.removeItem('session_token');
    sessionStorage.removeItem('session_token');
    toast.info('You must accept the Terms and Conditions to use the Partner Portal');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (alreadyAccepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <p className="text-gray-600">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_agent-earnings-5/artifacts/95ok324x_KB%20-%20Final%20Logo%20%28Updated%29-01.png"
            alt="KILO BEATERS"
            className="h-16 mx-auto mb-6"
          />
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-orange-500" />
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Terms and Conditions
            </h1>
          </div>
          <p className="text-gray-600 text-sm lg:text-base max-w-2xl mx-auto">
            Please read and accept the following Terms and Conditions to continue using the KiloBeaters Partner Portal.
          </p>
        </div>

        {/* T&C Content */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <ScrollArea 
            className="h-[50vh] lg:h-[60vh] p-6 lg:p-8"
            onScrollCapture={handleScroll}
          >
            <div className="prose prose-sm lg:prose-base max-w-none">
              {termsContent.split('\n').map((line, index) => {
                if (line.startsWith('# ')) {
                  return <h1 key={index} className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">{line.replace('# ', '')}</h1>;
                } else if (line.startsWith('## ')) {
                  return <h2 key={index} className="text-lg lg:text-xl font-semibold text-gray-800 mt-6 mb-3 border-b border-orange-200 pb-2">{line.replace('## ', '')}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={index} className="text-base lg:text-lg font-medium text-gray-700 mt-4 mb-2">{line.replace('### ', '')}</h3>;
                } else if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={index} className="font-semibold text-gray-800 my-2">{line.replace(/\*\*/g, '')}</p>;
                } else if (line.startsWith('|')) {
                  return null; // Skip table rows for simplicity
                } else if (line.startsWith('(')) {
                  return <p key={index} className="text-gray-600 ml-4 my-1">{line}</p>;
                } else if (line.startsWith('---')) {
                  return <hr key={index} className="my-6 border-gray-200" />;
                } else if (line.trim()) {
                  return <p key={index} className="text-gray-600 my-2 leading-relaxed">{line}</p>;
                }
                return null;
              })}
            </div>
          </ScrollArea>

          {/* Scroll indicator */}
          {!hasRead && (
            <div className="bg-amber-50 border-t border-amber-200 px-6 py-3 text-center">
              <p className="text-amber-700 text-sm">
                ↓ Please scroll down to read the entire document
              </p>
            </div>
          )}

          {/* Agreement section */}
          <div className="bg-gray-50 border-t border-gray-200 p-6">
            <div className="flex items-start gap-3 mb-6">
              <Checkbox
                id="agree"
                checked={agreed}
                onCheckedChange={setAgreed}
                disabled={!hasRead}
                className="mt-1"
              />
              <label 
                htmlFor="agree" 
                className={`text-sm lg:text-base cursor-pointer ${hasRead ? 'text-gray-700' : 'text-gray-400'}`}
              >
                I have read, understood, and agree to be bound by these Terms and Conditions. I acknowledge that this is a legally binding agreement.
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleDecline}
                className="order-2 sm:order-1"
              >
                Decline
              </Button>
              <Button
                onClick={handleAccept}
                disabled={!agreed || accepting}
                className="bg-orange-500 hover:bg-orange-600 order-1 sm:order-2"
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  'I Accept'
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-gray-500 text-xs mt-6">
          If you have any questions about these terms, please contact us at partners@kilobeaters.com
        </p>
      </div>
    </div>
  );
}
